import { Severity } from '@kirin/api-client/contracts/shared/severity';
import {
  formatSubPolicyDescription,
  formatSubPolicyLabel,
  formatSubPolicySections,
} from '@kirin/api-client/helpers/alerts';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import AlertCard from './AlertCard';
import { useUserPolicies } from '@/hooks/api/useUserPolicies';
import {
  PolicyKey,
  PolicyMode,
  type PolicyList,
} from '@kirin/api-client/contracts/policies';

const UNTRUSTED_MCP_CONFIG = `{
  "mcpServers": {
    "community-server": {
      "command": "npx",
      "args": ["mcp-server-database"]
    }
  }
}`;

const isMcpSourcesPolicy = (policy: { key: string }): boolean => {
  return policy.key === PolicyKey.McpServerSources;
};

const getMcpSourcesMode = (
  data: PolicyList['policies'] | undefined
): PolicyMode => {
  if (!data || data.length === 0) return PolicyMode.Off;
  const mcpPolicy = data.find(isMcpSourcesPolicy);
  return (mcpPolicy?.mode as PolicyMode | undefined) ?? PolicyMode.Off;
};

export default function BlockedMcpServerDemo() {
  const { isLoading, isError, data } = useUserPolicies({
    select: policies => policies.filter(isMcpSourcesPolicy),
  });

  const mcpSourcesMode = getMcpSourcesMode(
    data as PolicyList['policies'] | undefined
  );
  const showMCPInfo =
    !isLoading && !isError && mcpSourcesMode === PolicyMode.Off;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold mb-2">Steps to reproduce:</h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>
            Set the <span className="font-semibold">MCP Servers Security</span>{' '}
            policy to <span className="font-semibold">Monitor</span> or{' '}
            <span className="font-semibold">Enforce</span> in your
            organization&apos;s policy settings
          </li>
          <li>Copy the MCP server configuration below using the button</li>
          <li>
            Open or create:{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              .cursor/mcp.json
            </code>
          </li>
          <li>Paste the configuration into the file</li>
          <li>Save the file</li>
          <li>Check Kirin for the security alert</li>
        </ol>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">MCP server configuration:</h3>
          <CopyToClipboardButton
            text={UNTRUSTED_MCP_CONFIG}
            variant="outline"
            size="sm"
            className="gap-2"
          />
        </div>
        <HighlightBox
          variant="neutral"
          padding="lg"
          className="font-mono text-sm whitespace-pre-wrap overflow-x-auto"
        >
          {UNTRUSTED_MCP_CONFIG}
        </HighlightBox>
      </div>

      <div>
        <h3 className="font-semibold mb-2">
          What makes this server untrusted:
        </h3>
        <ul className="list-disc ml-5 space-y-1 text-sm text-muted-foreground">
          <li>
            Package{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              mcp-server-database
            </code>{' '}
            is not in Kirin's trusted packages list
          </li>
          <li>Not from the official {'@modelcontextprotocol'} namespace</li>
          <li>
            Name resembles trusted packages (mcp-server-*) but isn't explicitly
            approved — a common pattern in package impersonation attacks
          </li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold mb-2">
          Expected alert in Kirin (MCP Servers Security policy in
          Monitor/Enforce):
        </h3>
        <AlertCard
          title={formatSubPolicyLabel(
            'mcpServersUntrustedSource',
            'untrusted_mcp_server'
          )}
          severity={Severity.Medium}
          description={formatSubPolicyDescription(
            'mcpServersUntrustedSource',
            'untrusted_mcp_server',
            'MCP server source is an HTTP URL, a local file path, or an unrecognized package name.'
          )}
          descriptionSections={formatSubPolicySections(
            'mcpServersUntrustedSource',
            'untrusted_mcp_server'
          )}
          details="Source: community-server"
        />
      </div>
      {showMCPInfo && (
        <div>
          <h3 className="font-semibold mb-2">Important Note</h3>
          <HighlightBox variant={'info'}>
            To trigger an{' '}
            <span className="font-bold">Untrusted MCP Server</span> alert, set
            the <span className="font-bold">MCP Servers Security</span> policy
            to <span className="font-bold">Monitor</span> or{' '}
            <span className="font-bold">Enforce</span>.
          </HighlightBox>
        </div>
      )}
    </div>
  );
}
